/**
 * @module builder
 */
import type { QuoteDocument } from 'paintscout'
import type { FeatureProduct } from '../../options'
import { sortFeatureProducts } from '../../options/get-feature-products/get-feature-products'
import { applyWorkOrderCardOrder } from '../work-order-layout'

type ProductLayout = NonNullable<QuoteDocument['productLayout']>

/**
 * Feature products in the estimate's display order (ENG-2240): the saved drag order, which may mix
 * Sherwin-Williams and custom products (ENG-2237). Products the saved order doesn't know about
 * (added after the last drag) follow the ones it does, Sherwin-Williams first.
 */
export function applyProductLayoutOrder<T extends FeatureProduct>(quote: QuoteDocument, products: T[]): T[] {
  return applyWorkOrderCardOrder(sortFeatureProducts(products), quote?.productLayout?.order)
}

/** Whether the estimator hid this product from the estimate's Products section. */
export function isProductHidden(quote: QuoteDocument, key: string): boolean {
  return !!quote?.productLayout?.hidden?.includes(key)
}

/**
 * Drop the products the estimator hid. Every view that can't unhide a product uses this — the
 * presentation, the customer view, the preview dialog, a read-only estimate, and the classic
 * document — so a hidden product never reaches the customer by any of them.
 */
export function withoutHiddenProducts<T extends FeatureProduct>(quote: QuoteDocument, products: T[]): T[] {
  return products.filter((product) => !isProductHidden(quote, product.key))
}

/**
 * Move one product and return the whole list in its new order. Sherwin-Williams and custom products
 * share one list (ENG-2237). `products` is the full list in display order. `shown` is how many are on
 * screen ("View More" closed shows only the first few), and the indices count only those; the
 * products not on screen keep their place after them.
 */
export function moveFeatureProduct<T extends FeatureProduct>(args: {
  products: T[]
  shown: number
  oldIndex: number
  newIndex: number
}): T[] {
  const { products, shown, oldIndex, newIndex } = args
  const onScreen = products.slice(0, shown)
  const [moved] = onScreen.splice(oldIndex, 1)
  if (moved) {
    onScreen.splice(newIndex, 0, moved)
  }
  return [...onScreen, ...products.slice(shown)]
}

/**
 * Save the Products section's order. `keys` is the new order of the products being shown; keys
 * already saved for products no longer shown are kept behind them, so a product that comes back
 * (its area is restored) returns to where it was ranked.
 */
export function setProductOrder(args: { quote: QuoteDocument; keys: string[] }): QuoteDocument {
  const { quote, keys } = args
  const shown = new Set(keys)
  const others = (quote.productLayout?.order ?? []).filter((key) => !shown.has(key))
  return writeProductLayout(quote, { ...quote.productLayout, order: [...keys, ...others] })
}

/** Hide or unhide one product in the estimate's Products section. */
export function setProductHidden(args: { quote: QuoteDocument; key: string; hidden: boolean }): QuoteDocument {
  const { quote, key, hidden } = args
  const others = (quote.productLayout?.hidden ?? []).filter((k) => k !== key)
  return writeProductLayout(quote, { ...quote.productLayout, hidden: hidden ? [...others, key] : others })
}

/** Write the layout back onto the quote, dropping empty lists and the whole field once it is empty. */
function writeProductLayout(quote: QuoteDocument, layout: ProductLayout): QuoteDocument {
  const next: ProductLayout = {}
  if (layout.order?.length) {
    next.order = layout.order
  }
  if (layout.hidden?.length) {
    next.hidden = layout.hidden
  }
  const result = { ...quote, productLayout: next }
  if (!next.order && !next.hidden) {
    delete result.productLayout
  }
  return result
}
